import {
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react'
import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Building2,
  User,
  Truck,
  Store,
  Globe,
  Download,
  MoreHorizontal,
  ArrowLeft,
  ArrowRight,
  Upload,
  X,
} from 'lucide-react'
import handleSuccessToast from '../../../common/toast/handleSuccessToast'
import handleErrorToast from '../../../common/toast/handleErrorToast'
import { useHideTabBar } from '../../../../hooks/useHideTabBar'
import AddressAutocomplete, { type AddressFields } from '../../../common/AddressAutocomplete'
import { useCreateEntity, useUpdateEntity } from '../../../../hooks/useCustomEntities'
import { entityTypeColors, entityTypeLabels, type CustomEntity, type CustomEntityType, type EntityPreLink } from '../../../../types/customEntities.types'
import FormBrandMultiSelect from '../../../common/forms/FormBrandMultiSelect'

const MotionBox = motion(Box)

const typeCards: {
  key: CustomEntityType
  icon: typeof Building2
  description: string
}[] = [
  { key: 'person', icon: User, description: 'Individual or contact person' },
  { key: 'company', icon: Building2, description: 'Business or organization' },
  { key: 'supplier', icon: Truck, description: 'Product or service supplier' },
  { key: 'seller', icon: Store, description: 'Marketplace or direct seller' },
  { key: 'webshop', icon: Globe, description: 'Online store or e-commerce site' },
  { key: 'importer', icon: Download, description: 'Goods importer' },
  { key: 'exporter', icon: Upload, description: 'Goods exporter' },
  { key: 'other', icon: MoreHorizontal, description: 'Any other entity type' },
]

const AddEntityModal = ({
  isOpen,
  onClose,
  editEntity,
  preLink,
  onCreated,
}: {
  isOpen: boolean
  onClose: () => void
  editEntity?: CustomEntity
  preLink?: EntityPreLink
  onCreated?: (entity: CustomEntity) => void
}) => {
  const toast = useToast()
  const isEdit = !!editEntity

  const [step, setStep] = useState<'type' | 'details'>(isEdit ? 'details' : 'type')
  const [name, setName] = useState(editEntity?.name ?? '')
  const [nickname, setNickname] = useState(editEntity?.nickname ?? '')
  const [type, setType] = useState<CustomEntityType>(editEntity?.type ?? 'company')
  const [description, setDescription] = useState(editEntity?.description ?? '')
  const [url, setUrl] = useState(editEntity?.url ?? '')
  const [email, setEmail] = useState(editEntity?.email ?? '')
  const [phone, setPhone] = useState(editEntity?.phone ?? '')
  const [address, setAddress] = useState(editEntity?.address ?? '')
  const [city, setCity] = useState(editEntity?.city ?? '')
  const [postcode, setPostcode] = useState(editEntity?.postcode ?? '')
  const [country, setCountry] = useState(editEntity?.country ?? '')
  const [taxId, setTaxId] = useState(editEntity?.tax_id ?? '')
  const [language, setLanguage] = useState(editEntity?.language ?? '')
  const [addressType, setAddressType] = useState(editEntity?.address_type ?? '')
  const [brands, setBrands] = useState<{ id: string; name: string; logo: string | null | undefined }[]>(
    editEntity?.brands?.map((id) => ({ id, name: '', logo: null })) ?? [],
  )
  const [ptInput, setPtInput] = useState('')
  const [productTypes, setProductTypes] = useState<string[]>(editEntity?.product_types ?? [])

  const handleAddPt = () => {
    if (ptInput.trim() && !productTypes.includes(ptInput.trim())) {
      setProductTypes([...productTypes, ptInput.trim()])
      setPtInput('')
    }
  }

  const handleRemovePt = (pt: string) => {
    setProductTypes(productTypes.filter((t) => t !== pt))
  }

  const createMutation = useCreateEntity()
  const updateMutation = useUpdateEntity()

  const handleSubmit = () => {
    const payload = {
      name,
      nickname,
      type,
      description,
      url,
      email,
      phone,
      address,
      city,
      postcode,
      country,
      tax_id: taxId,
      product_types: productTypes,
      brands: brands.map((b) => b.id),
      language,
      address_type: addressType,
      ...(preLink && !isEdit ? { [preLink.field]: [preLink.id] } : {}),
    }
    if (isEdit) {
      updateMutation.mutateAsync({ id: editEntity.id, data: payload })
        .then(() => {
          handleSuccessToast(toast, 'Entity Updated!', 'The entity has been updated successfully.')
          onClose()
        })
        .catch(() => handleErrorToast(toast, 'Error', 'Failed to update entity.'))
    } else {
      createMutation.mutateAsync(payload)
        .then((created) => {
          handleSuccessToast(toast, 'Entity Created!', 'A new entity has been created.')
          onCreated?.(created)
          onClose()
        })
        .catch(() => handleErrorToast(toast, 'Error', 'Failed to create entity.'))
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const selectedColor = entityTypeColors[type]

  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false
  useHideTabBar(isMobile && isOpen)

  const typeSelectionGrid = (
    <SimpleGrid columns={{ base: 2, md: 3 } as any} spacing={{ base: '8px', md: '10px' } as any}>
      {typeCards.map((tc) => {
        const isSelected = type === tc.key
        const colors = entityTypeColors[tc.key]

        return (
          <MotionBox
            key={tc.key}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setType(tc.key)}
            cursor="pointer"
            p={{ base: '12px', md: '16px' } as any}
            borderRadius="12px"
            border="1px solid"
            borderColor={isSelected ? colors.color : 'neutral.100'}
            bg={isSelected ? colors.bg : 'white'}
            boxShadow={
              isSelected
                ? `0 0 0 2px ${colors.color}20`
                : '0px 2px 6px -4px rgba(15, 23, 42, 0.08)'
            }
            transition="border-color 0.15s, background 0.15s, box-shadow 0.15s"
            _hover={{
              borderColor: isSelected ? colors.color : 'neutral.200',
              boxShadow: isSelected
                ? `0 0 0 2px ${colors.color}20`
                : '0px 4px 10px -4px rgba(15, 23, 42, 0.12)',
            }}
          >
            <Center
              w={{ base: '32px', md: '36px' } as any}
              h={{ base: '32px', md: '36px' } as any}
              borderRadius="10px"
              bg={isSelected ? `${colors.color}18` : colors.bg}
              color={colors.color}
              mb={{ base: '8px', md: '10px' } as any}
            >
              <Icon as={tc.icon} boxSize={{ base: '16px', md: '18px' } as any} />
            </Center>
            <Text
              fontSize={{ base: '13px', md: 'sm' } as any}
              fontWeight="600"
              color={isSelected ? colors.color : 'neutral.900'}
              mb="2px"
              noOfLines={1}
            >
              {entityTypeLabels[tc.key]}
            </Text>
            <Text fontSize={{ base: '11px', md: 'xs' } as any} color="neutral.500" lineHeight="1.4">
              {tc.description}
            </Text>
          </MotionBox>
        )
      })}
    </SimpleGrid>
  )

  const preLinkLabel: Record<string, string> = {
    items: 'Item',
    sellers: 'Seller',
    saws: 'Site',
    seizures: 'Seizure',
    related_entities: 'Entity',
  }

  const detailsForm = (
    <>
      {preLink && (
        <Box mb="16px" px="12px" py="10px" bg="purple.50" borderRadius="8px" border="1px solid" borderColor="purple.100">
          <Text fontSize="13px" color="purple.700" fontWeight={500}>
            Will be linked to {preLinkLabel[preLink.field] ?? 'object'}:{' '}
            <Text as="span" fontWeight={700}>{preLink.label}</Text>
          </Text>
        </Box>
      )}
      <Box mb="16px">
        <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
          Entity Name *
        </Text>
        <Input
          height="40px"
          fontSize={{ base: '16px', md: '14px' } as any}
          placeholder="Enter entity name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Box>

      <Box mb="16px">
        <FormBrandMultiSelect
          value={brands}
          onChange={setBrands}
          name="brands"
        />
      </Box>

      <Box mb="16px">
        <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
          Nickname
        </Text>
        <Input
          height="40px"
          fontSize={{ base: '16px', md: '14px' } as any}
          placeholder="Optional nickname"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
        />
      </Box>

      <Box mb="16px">
        <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
          Description
        </Text>
        <Textarea
          placeholder="Describe this entity..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minH="80px"
          fontSize={{ base: '16px', md: '14px' } as any}
        />
      </Box>

      <Flex gap={{ base: '12px', md: '8px' } as any} mb="16px" direction={{ base: 'column', md: 'row' } as any}>
        <Box width={{ base: '100%', md: '50%' } as any}>
          <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
            Email
          </Text>
          <Input
            height="40px"
            fontSize={{ base: '16px', md: '14px' } as any}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Box>
        <Box width={{ base: '100%', md: '50%' } as any}>
          <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
            Phone
          </Text>
          <Input
            height="40px"
            fontSize={{ base: '16px', md: '14px' } as any}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+1 234 567 890"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Box>
      </Flex>

      <Box mb="16px">
        <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
          URL
        </Text>
        <Input
          height="40px"
          fontSize={{ base: '16px', md: '14px' } as any}
          type="url"
          inputMode="url"
          autoComplete="url"
          placeholder="https://..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </Box>

      <Box mb="16px">
        <AddressAutocomplete
          address={address}
          city={city}
          postcode={postcode}
          country={country}
          onChange={(fields: Partial<AddressFields>) => {
            if ('address' in fields) setAddress(fields.address!)
            if ('city' in fields) setCity(fields.city!)
            if ('postcode' in fields) setPostcode(fields.postcode!)
            if ('country' in fields) setCountry(fields.country!)
          }}
        />
      </Box>

      <Box mb="16px">
        <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
          Tax ID
        </Text>
        <Input
          height="40px"
          fontSize={{ base: '16px', md: '14px' } as any}
          placeholder="Tax ID"
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
        />
      </Box>

      <Flex gap={{ base: '12px', md: '8px' } as any} mb="16px" direction={{ base: 'column', md: 'row' } as any}>
        <Box width={{ base: '100%', md: '50%' } as any}>
          <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
            Language
          </Text>
          <Input
            height="40px"
            fontSize={{ base: '16px', md: '14px' } as any}
            placeholder="e.g., English, DE"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          />
        </Box>
        <Box width={{ base: '100%', md: '50%' } as any}>
          <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
            Address Type
          </Text>
          <Input
            height="40px"
            fontSize={{ base: '16px', md: '14px' } as any}
            placeholder="e.g., headquarters, warehouse"
            value={addressType}
            onChange={(e) => setAddressType(e.target.value)}
          />
        </Box>
      </Flex>

      <Box mb="12px">
        <Text mb="4px" textStyle="b-sm" fontWeight="400" color="neutral.900">
          Product Types
        </Text>
        {productTypes.length > 0 && (
          <Flex gap="6px" flexWrap="wrap" mb="8px">
            {productTypes.map((pt) => (
              <Tag key={pt} size="sm" bg="primary.10" color="primary.400" borderRadius="6px">
                <TagLabel>{pt}</TagLabel>
                <TagCloseButton onClick={() => handleRemovePt(pt)} />
              </Tag>
            ))}
          </Flex>
        )}
        <Flex gap="4px">
          <Input
            height="40px"
            fontSize={{ base: '16px', md: '14px' } as any}
            placeholder="Add a product type..."
            value={ptInput}
            onChange={(e) => setPtInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddPt()
              }
            }}
          />
          <Button height="40px" fontSize={{ base: '16px', md: '14px' } as any} fontWeight="510" onClick={handleAddPt}>
            Add
          </Button>
        </Flex>
      </Box>
    </>
  )

  if (isMobile && step === 'type') {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" autoFocus={false} blockScrollOnMount={true}>
        <DrawerOverlay bg="rgba(10, 10, 20, 0.55)" sx={{ backdropFilter: 'blur(4px)' }} />
        <DrawerContent borderTopRadius="20px" h="92svh" maxH="92svh">
          <Center pt="10px" pb="4px">
            <Box w="36px" h="4px" borderRadius="2px" bg="neutral.100" />
          </Center>
          <DrawerHeader p="8px 12px 12px 20px">
            <Flex align="center" gap="10px" h="40px">
              <Flex
                w="32px"
                h="32px"
                minW="32px"
                borderRadius="50%"
                bg="secondary.200"
                align="center"
                justify="center"
              >
                <Building2 size={16} color="white" />
              </Flex>
              <Box minW="0" flex="1">
                <Text fontSize="17px" fontWeight="600" color="neutral.900" letterSpacing="-0.01em">
                  New Entity
                </Text>
                <Text fontSize="13px" fontWeight="400" color="neutral.500">
                  Choose the type of entity you want to create
                </Text>
              </Box>
              <IconButton
                aria-label="Close"
                icon={<X size={20} strokeWidth={2} />}
                variant="ghost"
                onClick={onClose}
                h="40px"
                w="40px"
                minW="40px"
                color="neutral.700"
                bg="transparent"
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent', transform: 'scale(0.92)' }}
                transition="transform 120ms ease-out"
                sx={{ '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}
              />
            </Flex>
          </DrawerHeader>
          <Divider borderColor="neutral.100" />
          <DrawerBody p="16px 20px 20px" overflowY="auto">
            {typeSelectionGrid}
          </DrawerBody>
          <Divider borderColor="neutral.100" />
          <DrawerFooter p="12px 20px" gap="10px" sx={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            <Button
              flex="1"
              h="48px"
              fontSize="15px"
              fontWeight="600"
              variant="outline"
              borderColor="neutral.100"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              flex="2"
              h="48px"
              fontSize="15px"
              fontWeight="600"
              variant="heavy"
              rightIcon={<Icon as={ArrowRight} boxSize="14px" />}
              onClick={() => setStep('details')}
            >
              Continue
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }

  if (isMobile && step === 'details') {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" autoFocus={false} blockScrollOnMount={true}>
        <DrawerOverlay bg="rgba(10, 10, 20, 0.55)" sx={{ backdropFilter: 'blur(4px)' }} />
        <DrawerContent borderTopRadius="20px" h="92svh" maxH="92svh">
          <Center pt="10px" pb="4px">
            <Box w="36px" h="4px" borderRadius="2px" bg="neutral.100" />
          </Center>
          <DrawerHeader p="8px 12px 12px 20px">
            <Flex align="center" gap="10px" h="40px">
              <Flex
                w="32px"
                h="32px"
                minW="32px"
                borderRadius="50%"
                bg={selectedColor.bg}
                align="center"
                justify="center"
              >
                <Icon
                  as={typeCards.find((tc) => tc.key === type)?.icon ?? Building2}
                  boxSize="16px"
                  color={selectedColor.color}
                />
              </Flex>
              <Box minW="0" flex="1">
                <Text fontSize="17px" fontWeight="600" color="neutral.900" letterSpacing="-0.01em">
                  {isEdit ? 'Edit Entity' : `New ${entityTypeLabels[type]}`}
                </Text>
                <Text fontSize="13px" fontWeight="400" color="neutral.500">
                  {isEdit ? 'Update the entity details below' : 'Fill in the details below'}
                </Text>
              </Box>
              <IconButton
                aria-label="Close"
                icon={<X size={20} strokeWidth={2} />}
                variant="ghost"
                onClick={onClose}
                h="40px"
                w="40px"
                minW="40px"
                color="neutral.700"
                bg="transparent"
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent', transform: 'scale(0.92)' }}
                transition="transform 120ms ease-out"
                sx={{ '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}
              />
            </Flex>
          </DrawerHeader>
          <Divider borderColor="neutral.100" />
          <DrawerBody p="16px 20px 20px" overflowY="auto">
            {detailsForm}
          </DrawerBody>
          <Divider borderColor="neutral.100" />
          <DrawerFooter p="12px 20px" gap="10px" sx={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            {!isEdit ? (
              <Button
                flex="1"
                h="48px"
                fontSize="15px"
                fontWeight="600"
                variant="ghost"
                leftIcon={<Icon as={ArrowLeft} boxSize="14px" />}
                onClick={() => setStep('type')}
              >
                Back
              </Button>
            ) : (
              <Box flex="1" />
            )}
            <Button
              flex="1"
              h="48px"
              fontSize="15px"
              fontWeight="600"
              variant="outline"
              borderColor="neutral.100"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              flex="2"
              h="48px"
              fontSize="15px"
              fontWeight="600"
              variant="heavy"
              onClick={handleSubmit}
              isDisabled={!name}
              isLoading={isSubmitting}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      {/* This modal can be opened from inside the Octopus FAB overlay, which sits
          at the max int z-index. Lift the modal's portal container to match so it
          (as the later DOM sibling) paints above the overlay instead of behind it. */}
      <ModalOverlay zIndex={2147483647} />
      <ModalContent
        containerProps={{ zIndex: 2147483647 }}
        minW={{ base: 'auto', md: '616px' } as any}
        w={{ base: 'calc(100vw - 24px)', md: 'auto' } as any}
        maxW={{ base: 'calc(100vw - 24px)', md: '616px' } as any}
        maxH={{ base: 'calc(100dvh - 32px)', md: '90vh' } as any}
        m={{ base: '12px', md: '0' } as any}
        p="0"
      >
        <ModalHeader m="0" p={{ base: '12px 16px', md: '12px 24px' } as any} pr={{ base: '44px', md: '24px' } as any}>
          <Flex>
            <Center
              mt="5px"
              mb="5px"
              mr="4px"
              borderRadius="full"
              bg={step === 'type' ? 'secondary.200' : selectedColor.bg}
              minW="32px"
              w="32px"
              h="32px"
              flexShrink={0}
            >
              {step === 'type' ? (
                <Building2 size={16} color="white" />
              ) : (
                <Icon
                  as={typeCards.find((tc) => tc.key === type)?.icon ?? Building2}
                  boxSize="16px"
                  color={selectedColor.color}
                />
              )}
            </Center>
            <Center ml="8px" minW="0" flex="1">
              <Box minW="0" w="100%">
                <Text textStyle="b-sm" noOfLines={1}>
                  {isEdit
                    ? 'Edit Entity'
                    : step === 'type'
                      ? 'New Entity'
                      : `New ${entityTypeLabels[type]}`}
                </Text>
                <Text textStyle="b-xs" fontWeight="500" color="neutral.500" noOfLines={2}>
                  {isEdit
                    ? 'Update the entity details below'
                    : step === 'type'
                      ? 'Choose the type of entity you want to create'
                      : 'Fill in the details below'}
                </Text>
              </Box>
            </Center>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <Divider />

        {/* Step 1: Type Selection */}
        {step === 'type' && (
          <>
            <ModalBody p={{ base: '16px', md: '20px 24px' } as any} overflowY="auto">
              {typeSelectionGrid}
            </ModalBody>
            <Divider />
            <Flex justify={{ base: 'space-between', md: 'flex-end' } as any} gap={{ base: '8px', md: '0' } as any} p={{ base: '12px 16px', md: '12px 24px' } as any} pb={{ base: 'calc(12px + env(safe-area-inset-bottom, 0px))', md: '12px' } as any}>
              <Button
                fontSize={{ base: '16px', md: '14px' } as any}
                fontWeight="510"
                mr={{ base: '0', md: '16px' } as any}
                h={{ base: '44px', md: '40px' } as any}
                flex={{ base: '1', md: 'initial' } as any}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                fontSize={{ base: '16px', md: '14px' } as any}
                fontWeight="510"
                h={{ base: '44px', md: '40px' } as any}
                flex={{ base: '1', md: 'initial' } as any}
                variant="heavy"
                rightIcon={<Icon as={ArrowRight} boxSize="14px" />}
                onClick={() => setStep('details')}
              >
                Continue
              </Button>
            </Flex>
          </>
        )}

        {/* Step 2: Details Form */}
        {step === 'details' && (
          <>
            <ModalBody p={{ base: '12px 16px', md: '12px 24px' } as any} maxH={{ base: 'none', md: '600px' } as any} overflowY="auto">
              {detailsForm}
            </ModalBody>
            <Divider />

            <Flex
              justify="space-between"
              align="center"
              gap="8px"
              p={{ base: '12px 16px', md: '12px 24px' } as any}
              pb={{ base: 'calc(12px + env(safe-area-inset-bottom, 0px))', md: '12px' } as any}
            >
              {!isEdit ? (
                <Button
                  fontSize={{ base: '13px', md: '14px' } as any}
                  fontWeight="510"
                  h={{ base: '44px', md: '40px' } as any}
                  px={{ base: '10px', md: '16px' } as any}
                  variant="ghost"
                  leftIcon={<Icon as={ArrowLeft} boxSize="14px" />}
                  onClick={() => setStep('type')}
                  flexShrink={0}
                >
                  Back
                </Button>
              ) : (
                <Box />
              )}
              <Flex gap={{ base: '8px', md: '12px' } as any} flex={{ base: '1', md: 'initial' } as any} justify="flex-end">
                <Button
                  fontSize={{ base: '16px', md: '14px' } as any}
                  fontWeight="510"
                  h={{ base: '44px', md: '40px' } as any}
                  flex={{ base: '1', md: 'initial' } as any}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  fontSize={{ base: '16px', md: '14px' } as any}
                  fontWeight="510"
                  h={{ base: '44px', md: '40px' } as any}
                  flex={{ base: '1', md: 'initial' } as any}
                  variant="heavy"
                  onClick={handleSubmit}
                  isDisabled={!name}
                  isLoading={isSubmitting}
                >
                  {isEdit ? 'Save' : 'Create'}
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default AddEntityModal
